import { useCallback, useState } from 'react';

import { useTracking } from '@change-corgi/core/react/tracking';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import type { MutationState } from 'src/app/shared/utils/async';

import { completePetitionTask } from './api';
import type { CompleteTaskErrorCode, TaskType } from './constants';
import { COMPLETE_TASK_ERROR_MAP, completeTaskErrorCodes } from './constants';
import { getTrackingDataForTask } from './utils';

type RequestState = MutationState<{ data: { success: boolean } }, { error: CompleteTaskErrorCode }>;

type UseCompleteTaskOptions = {
	onSuccess?: () => void;
	onError?: (error: CompleteTaskErrorCode) => void;
	completionType?: 'manual' | 'automatic';
	version?: 'old' | 'new_12_2025';
};
type UseCompleteTaskReturn = RequestState & {
	mutate: (
		taskId: string,
		taskName: TaskType,
	) => Promise<Extract<RequestState, { status: 'loaded' } | { status: 'error' }>>;
	reset: () => void;
};

const COMPLETE_TASK_TRACKING_EVENT = 'petition_manage_tasks_completed';

export function useCompleteTask({
	onSuccess,
	onError,
	completionType = 'manual',
	version = 'old',
}: UseCompleteTaskOptions = {}): UseCompleteTaskReturn {
	const utilityContext = useUtilityContext();
	const [state, setState] = useState<RequestState>({ status: 'idle' });
	const track = useTracking();
	const handleCompleteTask = useCallback(
		async (taskId: string, taskName: TaskType) => {
			setState({ status: 'loading' });

			try {
				const result = await completePetitionTask(taskId, utilityContext);
				const successStatus = { status: 'loaded' as const, data: result };
				setState(successStatus);
				onSuccess?.();
				const trackingData = getTrackingDataForTask(taskName);
				void track(COMPLETE_TASK_TRACKING_EVENT, {
					completed: completionType,
					version,
					...trackingData,
				});
				return successStatus;
			} catch (e) {
				const errorMessage = parseError(e);
				const errorStatus = { status: 'error' as const, error: errorMessage };
				setState(errorStatus);
				onError?.(errorMessage);
				return errorStatus;
			}
		},
		[onError, onSuccess, utilityContext, completionType, track, version],
	);

	const handleReset = useCallback(() => {
		setState({ status: 'idle' });
	}, []);

	return { mutate: handleCompleteTask, reset: handleReset, ...state };
}

function parseError(error: unknown) {
	const err = error instanceof Error ? error.message : undefined;
	return completeTaskErrorCodes.find((val) => val === err) ?? COMPLETE_TASK_ERROR_MAP.UNKNOWN_ERROR.code;
}
